import React, { useEffect, useRef, useState } from "react";

function Usavisitvisaimport() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      {/* Left-side Navigation */}
      <div className="sticky top-0 h-screen w-1/4 bg-gray-100 p-4 hidden md:block">
        <ul className="space-y-4">
          <li
            onClick={() => scrollToSection(0)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 0
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Determine Visa Type
          </li>
          <li
            onClick={() => scrollToSection(1)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 1
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Pay the Visa Fee
          </li>
          <li
            onClick={() => scrollToSection(2)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 2
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Schedule Appointments
          </li>
          <li
            onClick={() => scrollToSection(3)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 3
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Required Documents
          </li>
          <li
            onClick={() => scrollToSection(4)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 4
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Entry to the U.S.
          </li>
        </ul>
      </div>

      {/* Content Section */}
      <div
        ref={contentRef}
        className="mb-10 h-screen overflow-y-auto p-4 w-full md:w-3/4"
      >
        <div ref={(el) => (sectionRefs.current[0] = el)}>
          <p className="text-xl font-bold text-[#011473]">
            How to Apply for a USA Visit Visa
          </p>
          <p className="mt-4 text-lg font-semibold text-[#011473]">
            Step 1: Determine Your Visa Type
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              Identify the appropriate visa category based on your purpose of
              travel (e.g., non-immigrant B1/B2 visa).
            </li>
            <li>
              Complete the DS-160 Non-Immigrant Visa Application form with
              accurate details. This form is critical for visa approval.
            </li>
            <li>
              Seek guidance from an immigration consultant or legal advisor if
              needed.
            </li>
            <li>
              Check if your country qualifies for the Visa Waiver Program (VWP).
              If eligible, apply for authorization via the Electronic System for
              Travel Authorization (ESTA) before traveling by air or sea.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Step 2: Pay the Visa Fee
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              After completing the DS-160 form, pay the visa application fee
              online.
            </li>
            <li>Payments can be made in U.S. dollars or your local currency.</li>
            <li>
              Keep the receipt as it contains a number needed for booking your
              visa appointment.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Step 3: Schedule Visa Appointments
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              Book two appointments:
              <ul className="mt-2 list-inside list-disc">
                <li>
                  <strong>Visa Application Center (VAC):</strong> For biometrics
                  and photograph submission.
                </li>
                <li>
                  <strong>U.S. Embassy or Consulate:</strong> For the visa
                  interview.
                </li>
              </ul>
            </li>
            <li>
              Retain the DS-160 confirmation number for reference throughout the
              process.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Step 4: Required Documents for Visa Interview
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Valid passport.</li>
            <li>Visa application fee receipt.</li>
            <li>DS-160 confirmation page.</li>
            <li>A recent photograph meeting U.S. visa specifications.</li>
            <li>
              Financial documents (e.g., bank statements, tax returns, or proof
              of assets).
            </li>
            <li>Appointment confirmation page.</li>
            <li>Health records or medical reports if applicable.</li>
            <li>
              Employment verification letter, travel itinerary and character
              certificate.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[4] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Entry to the United States
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              A visa allows you to travel to a U.S. port of entry (e.g.,
              airport) to request permission to enter the country.
            </li>
            <li>
              Entry is determined by U.S. Customs and Border Protection (CBP)
              officials.
            </li>
            <li>
              If granted, you will receive an admission stamp or a Form I-94
              (Arrival/Departure Record).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Usavisitvisaimport;
